import React, { FC, RefObject, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { MyAccountContainer } from '@components/organisms/MyAccountContainer';
import { Select, SelectType } from '@components/atoms/Select';
import { Input, InputTypes, InputVariants } from '@components/atoms/Input';
import { Typography, TypographyTagVariants } from '@components/atoms/Typography';
import { Form } from '@components/atoms/Form';
import { Table } from '@components/atoms/Table';
import { Anchor, AnchorMode } from '@components/atoms/Anchor';
import { Pagination } from '@components/molecules/Pagination';
import { Card } from '@components/atoms/Card';
import { ProductStateBadge } from '@components/molecules/badges/ProductStateBadge';
import { PopupDelete } from '@components/molecules/PopupDelete';
import { SITE_URLS } from '@constants';
import { useMediaQuery } from '@hooks/useMediaQuery';
import { usePermissionChecker, UserPermissions } from '@hooks/useHasPermission';
import { useCustomNavigate } from '@hooks/useCustomNavigate';
import { useData } from '@hooks/useData';
import { useFilters } from '@hooks/useFilters';
import { useInfiniteScroll } from '@hooks/useInfiniteScroll';
import { useNotifications } from '@hooks/useNotifications';
import { formatPrice } from '@utils/format';
import { useGetMyAccountProducts } from '@services/useGetMyAccountProducts';
import { useDeleteProduct } from '@services/useDeleteProduct';
import { useGetAllProductsCategories } from '@services/useGetAllProductsCategories';
import { useGetAllMyAccountStores } from '@services/useGetAllMyAccountStores';
import { ResultsType } from '@types';
import styles from './ManageProducts.module.css';

const TEXT_FILTER_ID = 'text-filter';
const CATEGORY_FILTER_ID = 'category-filter';
const STORE_FILTER_ID = 'store-filter';

export const ManageProducts: FC = () => {
    const { navigate } = useCustomNavigate();
    const { can } = usePermissionChecker();
    const { pageToRequest, resetPageToRequest, checkPageToRequest, observerRef: scrollObserverRef } = useInfiniteScroll();
    const { t } = useTranslation();

    const translations = {
        title: t('manage-products-component.title'),
    };

    // if user does not have permission accessMerchantAreas, he can't access this page
    useEffect(() => {
        if (can(UserPermissions.accessMerchantAreas) === false) {
            navigate(`/${SITE_URLS.myProfile}`);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const formRef = useRef<HTMLFormElement>(null);

    return (
        <MyAccountContainer title={translations.title}>
            <>
                <div className="pt-4 pb-2">
                    <Filters formRef={formRef} pageToRequest={pageToRequest} checkPageToRequest={checkPageToRequest} />
                </div>
                <div className="pt-4">
                    <ManagementTable formRef={formRef} resetPageToRequest={resetPageToRequest} />
                    <div ref={scrollObserverRef} style={{ height: '1px' }} />
                </div>
            </>
        </MyAccountContainer>
    );
};

interface FiltersProps {
    formRef: RefObject<HTMLFormElement>;
    pageToRequest: number;
    checkPageToRequest: () => void;
}

const Filters: FC<FiltersProps> = ({ formRef, pageToRequest, checkPageToRequest }) => {
    const location = useLocation();
    const pageSize = useMediaQuery();
    const { request: requestMyAccountProducts } = useGetMyAccountProducts();
    const { myAccountProducts, isLoadingMyAccountProducts, setMyAccountProducts } = useData();
    const { appliedFilters, results, setResults, currentPage, setCurrentPage, setFilterValues } = useFilters();
    const { t } = useTranslation();

    const translations = {
        addProduct: t('manage-products-component.add-product'),
    };

    useEffect(() => {
        // when unmounting page, reset results
        // to prevent showing old results when returning to this page
        return () => {
            setResults([]);
            setMyAccountProducts(null);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getTextFilter = appliedFilters.find((item) => item.label === TEXT_FILTER_ID)?.values[0].name;
    const getCategoryFilter = appliedFilters
        .find((item) => item.label === CATEGORY_FILTER_ID)
        ?.values.map((value) => value.name)?.[0];
    let getStoreFilter = appliedFilters.find((item) => item.label === STORE_FILTER_ID)?.values.map((value) => value.name)?.[0];

    useEffect(() => {
        // check if url states a specific store, initially
        const queryParams = new URLSearchParams(location.search);
        const getStoreParam = queryParams.get('store') || '';
        if (getStoreParam) {
            // eslint-disable-next-line react-hooks/exhaustive-deps
            getStoreFilter = getStoreParam;
            setFilterValues(STORE_FILTER_ID, [{ uuid: null, name: getStoreParam }]);
        }
    }, []);

    const fetchProducts = (page: number) => {
        setCurrentPage(page);

        // prevent calls for pages that don't exist
        if (myAccountProducts?.pagination?.total_pages && page > myAccountProducts?.pagination?.total_pages) {
            return;
        }

        requestMyAccountProducts({
            textSearch: getTextFilter,
            categories_uuid: getCategoryFilter,
            stores_uuid: getStoreFilter,
            page: page,
        });
    };

    // request more products from next page
    useEffect(() => {
        pageToRequest > currentPage && !isLoadingMyAccountProducts && fetchProducts(currentPage + 1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pageToRequest, isLoadingMyAccountProducts]);

    // request first page whenever a filter changes
    const submitForm = () => fetchProducts(1);

    useEffect(() => {
        !isLoadingMyAccountProducts && formRef?.current?.requestSubmit();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appliedFilters]);

    // every time the endpoint is called, append the data to results constant
    // if the page being called is the first page, reset results
    useEffect(() => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        setResults((previous: any[]) => {
            return myAccountProducts?.pagination?.current_page === 1
                ? myAccountProducts?.data || []
                : [...previous, ...(myAccountProducts?.data || [])];
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [myAccountProducts]);

    // manually check if next page should be requested
    // in case user's screen is big and isn't possible to scroll to trigger next page request
    useEffect(() => {
        checkPageToRequest();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [results.length]);

    return (
        <MyAccountContainer.Filters>
            <Form formRef={formRef} onSubmit={submitForm}>
                <TextFilter />
                <div className={`d-flex flex-column flex-md-row ${styles['grouped-select-filters']}`}>
                    <CategoryFilter />
                    <StoreFilter />
                </div>
                {/* add product */}
                <div>
                    <Anchor
                        href={SITE_URLS.myProductsCreate}
                        classes={['xs', 'sm'].includes(pageSize) ? 'w-100' : ''}
                        mode={AnchorMode.primary}>
                        {translations.addProduct}
                    </Anchor>
                </div>
            </Form>
        </MyAccountContainer.Filters>
    );
};

interface ManagementTableProps {
    formRef: RefObject<HTMLFormElement>;
    resetPageToRequest: () => void;
}

const ManagementTable: FC<ManagementTableProps> = ({ formRef, resetPageToRequest }) => {
    const { showDefaultNotification } = useNotifications();
    const {
        request: requestDelete,
        loading: loadingDelete,
        error: errorDelete,
        success: successDelete,
        lastResponseTime,
    } = useDeleteProduct();
    const { results: products } = useFilters();
    const { isLoadingMyAccountProducts } = useData();
    const { t } = useTranslation();

    const translations = {
        name: t('manage-products-component.table.name'),
        merchant: t('manage-products-component.table.merchant'),
        category: t('manage-products-component.table.category'),
        price: t('manage-products-component.table.price'),
        vatIncluded: t('vat-included'),
        condition: t('manage-products-component.table.condition'),
        actions: t('manage-products-component.table.actions'),
        deletePopupTitle: t('manage-products-component.delete-popup.title'),
        deletePopupDescription: t('manage-products-component.delete-popup.description'),
        deletePopupWarning: t('manage-products-component.delete-popup.warning'),
    };

    // uuid of product being removed
    const [showDeleteModal, setShowDeleteModal] = useState<string | null>(null);

    // dispatch notification, according to feedback of service
    useEffect(() => {
        if (lastResponseTime) {
            // close delete modal
            showDeleteModal !== null && setShowDeleteModal(null);

            if (errorDelete) {
                showDefaultNotification({ type: 'error', message: errorDelete });
            } else {
                if (successDelete) {
                    showDefaultNotification({ type: 'success', message: successDelete });
                }
                // reset paginated list of products
                formRef?.current?.requestSubmit();
                resetPageToRequest();
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lastResponseTime]);

    const handleOnConfirmDelete: () => void = () => {
        showDeleteModal && requestDelete({ uuid: showDeleteModal });
    };

    return (
        <div data-testid="management-table">
            <Card classes="mb-0">
                <Table
                    borderless={true}
                    headerCols={[
                        { classes: 'text-left', text: translations.name },
                        { classes: 'text-center', hideOnMobile: true, text: translations.merchant },
                        { classes: 'text-center', hideOnMobile: true, text: translations.category },
                        { classes: 'text-end', hideOnMobile: true, text: translations.price },
                        { classes: 'text-center', text: translations.condition },
                        { classes: 'text-end', fit: true, text: translations.actions },
                    ]}
                    contentRows={products.map((item) => {
                        const price = formatPrice(item.price);

                        return [
                            {
                                classes: 'text-left',
                                content: <Typography fontSize="14">{item.name}</Typography>,
                            },
                            {
                                classes: 'text-center',
                                content: <Typography fontSize="14">{item.merchant}</Typography>,
                            },
                            {
                                classes: 'text-center',
                                content: <Typography fontSize="14">{item.category}</Typography>,
                            },
                            {
                                classes: 'text-end',
                                content: (
                                    <div className="d-inline-flex flex-column align-items-end">
                                        <Typography fontSize="14" classes="mb-0">
                                            {price || '-'}
                                        </Typography>
                                        {price && (
                                            <Typography fontSize="12" color="muted" classes="mb-0">
                                                {translations.vatIncluded}
                                            </Typography>
                                        )}
                                    </div>
                                ),
                            },
                            {
                                classes: 'text-center',
                                content: <ProductStateBadge state={item.status || ''} />,
                            },
                            {
                                classes: 'text-end',
                                content: (
                                    <Table.Options
                                        options={[
                                            <Anchor
                                                key="option-1"
                                                href={SITE_URLS.myProductsEdit.replace(':uuid', `${item.uuid}`)}
                                                mode={AnchorMode.none}>
                                                <Typography fontSize="16" tagVariant={TypographyTagVariants.span}>
                                                    <i className="fa-light fa-pen-to-square" />
                                                </Typography>
                                            </Anchor>,
                                            <button
                                                key="option-2"
                                                disabled={loadingDelete}
                                                onClick={() => setShowDeleteModal(item.uuid || null)}>
                                                <Typography fontSize="16" tagVariant={TypographyTagVariants.span}>
                                                    <i className="fa-light fa-trash-can" />
                                                </Typography>
                                            </button>,
                                        ]}
                                    />
                                ),
                            },
                        ];
                    })}
                />
            </Card>
            <Pagination loading={isLoadingMyAccountProducts} type={ResultsType.products} />

            {/* popup to confirm deletion of variant */}
            <PopupDelete
                question={translations.deletePopupTitle}
                confirmation={translations.deletePopupDescription}
                warning={translations.deletePopupWarning}
                onClickConfirm={handleOnConfirmDelete}
                show={showDeleteModal !== null}
                onHide={() => setShowDeleteModal(null)}
            />
        </div>
    );
};

const TextFilter: FC = () => {
    const pageSize = useMediaQuery();
    const { appliedFilters, setFilterValues } = useFilters();
    const { t } = useTranslation();

    const translations = {
        searchPlaceholder: t('manage-products-component.search.placeholder'),
    };

    return (
        <div className="align-content-center" style={{ width: ['xs', 'sm', 'md'].includes(pageSize) ? '100%' : 'unset' }}>
            <Input
                type={InputTypes.text}
                variant={InputVariants.searchInput}
                placeholder={translations.searchPlaceholder}
                value={appliedFilters.find((item) => item.label === TEXT_FILTER_ID)?.values[0].name || ''}
                onChange={(value) => setFilterValues(TEXT_FILTER_ID, [{ uuid: null, name: value }])}
            />
        </div>
    );
};

const CategoryFilter: FC = () => {
    const pageSize = useMediaQuery();
    const { appliedFilters, setFilterValues } = useFilters();
    const { allProductsCategories, isLoadingAllProductsCategories, isLoadingMyAccountProducts } = useData();
    const { request: requestAllProductsCategories } = useGetAllProductsCategories();
    const { t } = useTranslation();

    const translations = {
        title: t('manage-products-component.category.label'),
        default: t('select-default-option'),
    };

    // call services, if we don't have the information yet
    useEffect(() => {
        !allProductsCategories && !isLoadingAllProductsCategories && requestAllProductsCategories();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allProductsCategories]);

    const options = [
        { groupName: '', groupOptions: [{ value: '', text: translations.default }] },
        ...(allProductsCategories?.data || []).map((group) => {
            return {
                groupName: group.name || '',
                groupOptions:
                    group.children?.map((item) => {
                        return { value: item.uuid || '', text: item.name || '' };
                    }) || [],
            };
        }),
    ];

    const selected = appliedFilters.find((item) => item.label === CATEGORY_FILTER_ID)?.values.map((value) => value.name)?.[0];

    const handleOnChange: (value: string) => void = (value) => setFilterValues(CATEGORY_FILTER_ID, [{ uuid: null, name: value }]);

    return (
        <div className="d-flex align-items-center">
            <Typography classes="text-nowrap mb-0 me-2" fontSize="16">
                {translations.title}
            </Typography>
            <Select
                classes={['xs', 'sm'].includes(pageSize) ? 'flex-grow-1' : ''}
                width={['xs', 'sm'].includes(pageSize) ? '100%' : '146px'}
                type={SelectType.variant2}
                disabled={allProductsCategories === null || isLoadingAllProductsCategories || isLoadingMyAccountProducts}
                options={options}
                value={selected}
                onChange={handleOnChange}
            />
        </div>
    );
};

const StoreFilter: FC = () => {
    const pageSize = useMediaQuery();
    const { appliedFilters, setFilterValues } = useFilters();
    const { request: requestAllMyAccountStores } = useGetAllMyAccountStores();
    const { allMyAccountStores, isLoadingAllMyAccountStores, isLoadingMyAccountProducts } = useData();
    const { t } = useTranslation();

    const translations = {
        title: t('manage-products-component.store.label'),
        default: t('select-default-option'),
    };

    // call services, if we don't have the information yet
    useEffect(() => {
        !allMyAccountStores && !isLoadingAllMyAccountStores && requestAllMyAccountStores();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allMyAccountStores]);

    const options = [
        { value: '', text: translations.default },
        ...(allMyAccountStores?.data || [])
            // filter by stores that may have products
            .filter((item) => item.can_have_products)
            .map((item) => {
                return { value: item.uuid || '', text: item.name || '' };
            }),
    ];

    const selected = appliedFilters.find((item) => item.label === STORE_FILTER_ID)?.values.map((value) => value.name)?.[0];

    const handleOnChange: (value: string) => void = (value) => setFilterValues(STORE_FILTER_ID, [{ uuid: null, name: value }]);

    return (
        <div className="d-flex align-items-center">
            <Typography classes="text-nowrap mb-0 me-2" fontSize="16">
                {translations.title}
            </Typography>
            <Select
                classes={['xs', 'sm'].includes(pageSize) ? 'flex-grow-1' : ''}
                width={['xs', 'sm'].includes(pageSize) ? '100%' : '146px'}
                type={SelectType.variant2}
                options={options}
                disabled={allMyAccountStores === null || isLoadingAllMyAccountStores || isLoadingMyAccountProducts}
                value={selected}
                onChange={handleOnChange}
            />
        </div>
    );
};
